<template>
    <div class="backoffice-trainers-index">
        <div class="page-header">
            <div class="left">
                <h2 class="page-title"> {{ $page.props.lang.trainers || "List of Trainers" }}</h2>
            </div>
            <div class="right">
                <v-btn color="primary" dark @click="newTrainerModal = true">
                    <v-icon small>mdi-plus</v-icon>
                    <span v-if="$vuetify.breakpoint.mdAndUp" class="ml-2">{{ $page.props.lang.newTrainer || "New trainer" }}</span>
                    </v-btn>
            </div>
        </div>
        <div class="filter-section">
            <v-row>
                <v-col class="d-flex" cols="12" sm="3">
                   <v-text-field v-model="form.search" :label="$page.props.lang.filter_search || 'Search'" background-color="#FFFFFF" outlined dense prepend-inner-icon="mdi-magnify" class="table-search"></v-text-field>
                </v-col>
                <v-col class="d-flex" cols="12" sm="3">
                     <v-select v-model="form.status" item-value="value" item-text="text" label="Status" :items="statuses" dense outlined background-color="#FFFFFF"></v-select>
                </v-col>
            </v-row>
        </div>
        <v-dialog v-model="newTrainerModal" max-width="500px">
            <v-card>
                <div class="modal-header">
                    <h4 class="modal-headline">{{ $page.props.lang.newTrainer || "New trainer" }}</h4>
                    <div class="tools">
                        <v-btn icon @click="newTrainerModal = false"><v-icon>mdi-close</v-icon></v-btn>
                    </div>
                </div>
                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <p class="custom-label">{{ $page.props.lang.firstName || 'First name' }}</p>
                                <v-text-field v-model="newTrainer.first_name"
                                outlined dense :hide-details="!responseErrors.first_name"
                                :error-messages="responseErrors.first_name" @focus="responseErrors.first_name=''"></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <p class="custom-label">{{ $page.props.lang.lastName || 'Last name' }}</p>
                                <v-text-field v-model="newTrainer.last_name"
                                outlined dense :hide-details="!responseErrors.last_name"
                                :error-messages="responseErrors.last_name" @focus="responseErrors.last_name=''"></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <p class="custom-label">{{ $page.props.lang.birthDate || 'Day of birth' }}</p>
                                <v-menu
                                ref="birthDatePicker"
                                v-model="birthDatePicker"
                                :close-on-content-click="false"
                                :return-value.sync="date"
                                transition="scale-transition"
                                offset-y
                                min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="formatDatePicker"
                                        append-icon="mdi-menu-down" v-bind="attrs" v-on="on"
                                        outlined dense readonly :hide-details="!responseErrors.birth_date"
                                        @click:append="birthDatePicker = true" class="birth-date"></v-text-field>
                                    </template>
                                    <v-date-picker ref="picker"
                                    v-model="newTrainer.birth_date"
                                    :min="minBirth" :max="yesterday"
                                    :active-picker.sync="activePicker"
                                    :first-day-of-week="1" no-title scrollable color="primary">
                                        <v-spacer></v-spacer>
                                        <v-btn color="regular" @click="birthDatePicker = false">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                                        <v-btn color="primary" @click="birthDatePicker = false">{{ $page.props.lang.ok || "Ok" }}</v-btn>
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12">
                                <p class="custom-label">{{ $page.props.lang.gender || 'Gender' }}</p>
                                <v-select v-model="newTrainer.gender"
                                :items="gender"
                                :error-messages="responseErrors.gender" @change="responseErrors.gender=''"
                                :hide-details="!responseErrors.gender"
                                outlined dense></v-select>
                            </v-col>
                            <v-col cols="12">
                                <p class="custom-label">{{ $page.props.lang.email || 'E-mail' }}</p>
                                <v-text-field v-model="newTrainer.email"
                                :error-messages="responseErrors.email"
                                @focus="responseErrors.email=''"
                                :hide-details="!responseErrors.email"
                                outlined dense solo flat></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <p class="custom-label">{{ $page.props.lang.phone || 'Phone number' }}</p>
                                <v-text-field :placeholder="country_code + 'xxxx...'" v-model="newTrainer.phone_number"
                                :error-messages="responseErrors.phone_number"
                                @focus="responseErrors.phone_number=''"
                                :hide-details="!responseErrors.phone_number"
                                outlined dense solo flat></v-text-field>
                                <v-switch v-model="newTrainer.createAccount" :label="$page.props.lang.sendInvitation || `Send invitation to create an account`"></v-switch>
                            </v-col>
                        </v-row>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn @click="newTrainerModal = false" color="regular">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                            <v-btn @click="createNew" :loading="saving" color="primary">{{ $page.props.lang.save || "Save" }}</v-btn>
                        </v-card-actions>
                    </v-container>
                </v-card-text>
            </v-card>
        </v-dialog>

        <div class="table-container">
            <v-data-table
            :headers="computedHeaders"
            :items="members.data"
            :server-items-length="members.total"
            :sort-by.sync="options.sortBy"
            :sort-desc.sync="options.sortDesc"
            :page="members.current_page"
            :options.sync="options"
            :items-per-page="options.itemsPerPage"
            item-key="item.id"
            mobile-breakpoint="0"
            :footer-props="{
                    'items-per-page-options': [5, 10, 15]
                }"

            >
                <template v-slot:item.first_name="{ item }">
                    <inertia-link :href="'/backoffice/trainers/details/'+item.id" class="name">
                        <v-avatar size="40">
                            <img :src="item.profile_photo_url + '?w=80'" alt="">
                        </v-avatar>
                        {{ item.first_name }} {{ item.last_name }}
                    </inertia-link>
                </template>
                <template v-slot:item.birth_date="{ item }">
                    {{ item.birth_date | formatDate }}
                </template>
                <template v-slot:item.gender="{ item }">
                    {{ getGender(item.gender) }}
                </template>
                <template v-slot:item.phone_number="{ item }">
                    {{ item.phone_number }}
                </template>
                <template v-slot:item.email="{ item }">
                    {{ item.email }}
                </template>
            </v-data-table>
        </div>
        <!-- <pagination :links="members.links" /> -->
    </div>
</template>

<script>
import AppLayout from '@/js/Layouts/AppLayout'
import Pagination from '@/js/Components/Pagination'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'
import EventBus from '@/js/eventbus'

export default {
    metaInfo() {
        return {
            title:  this.$page.props.lang.title || "List of Trainers"
        }
    },
    layout: AppLayout,
    components: {
        Pagination
    },
    props: {
        members: Object,
        filters: Object,
        lang: Object,
        country_code: String
    },
    data(){
        return {
            membersHeaders: [
                {text: this.lang.fullName || 'Full Name', value: 'first_name', align: 'start'},
                {text: this.lang.birthDate || 'Birth Date', value: 'birth_date', sortable: false},
                {text: this.lang.gender || 'Gender', value: 'gender', sortable: false},
                {text: this.lang.email || 'Email', value: 'email', sortable: false},
                {text: this.lang.phone || 'Phone Number', value: 'phone_number', sortable: false}
            ],
            date: moment().format('YYYY-MM-DD'),
            birthDatePicker: false,
            activePicker: null,
            minBirth: moment().subtract(70, 'years').format('YYYY-MM-DD'),
            yesterday: moment().subtract(1, 'days').format('YYYY-MM-DD'),
            saving: false,
            newTrainerModal: false,
            newTrainer: {
                first_name: '',
                last_name: '',
                birth_date: null,
                gender: '',
                email: '',
                phone_number: '',
                createAccount: false
            },
            responseErrors: {},
            messageObjects: [],
            page: null,
            options:{page:this.members.current_page},
            sortBy: [],
            sortDesc: [],
            search: null,
            form: {
                search: this.filters.search,
                status: this.filters.status
            },
            statuses:[
                {'text': this.lang.all_trainers || 'All Trainers' , value: "0"},
                {'text': this.lang.all_active || "Only Active", value: "1"},
                {'text': this.lang.all_disabled || "Only Inactive", value: "-1" }
            ],

        }
    },
    computed: {
        gender() {
            let male = this.lang.male || 'Male';
            let female = this.lang.female || 'Female';
            let other = this.lang.other || 'Other';
            return [
                { text: male, value: 0 },
                { text: female, value: 1 },
                { text: other, value: 2 }

            ]
        },
        formattedDate() {
            let realDate = new Date(this.newTrainer.birth_date)
            let month = realDate.getMonth()
            let day = realDate.getDate()
            let year = realDate.getFullYear()
            return day + '.' + month + '.' + year + '.'
        },

        formatDatePicker(){
            if(this.newTrainer.birth_date !== null){
                return moment(this.newTrainer.birth_date).format('D.M.Y.');
            }else{
                return null;
            }
        },

        computedHeaders() {
            if (this.$vuetify.breakpoint.width > 599) {
                return this.membersHeaders
            } else {
                return this.membersHeaders.filter(item => item.value === 'first_name')
            }
        }
    },
    watch: {
        form: {
            handler: throttle(function() {
                this.$inertia.get("/backoffice/trainers", pickBy(this.form), { preserveState: true })
            }, 250),
            deep: true
        },
        options:{

             handler (val, oldVal) {

                if(val.page != oldVal.page || val.sortBy != oldVal.sortBy || val.sortDesc != oldVal.sortDesc || val.itemsPerPage != oldVal.itemsPerPage){

                    let data = {}
                    let order = this.options.sortDesc[0]===false ? 'asc' : 'desc';

                    if (this.options.sortDesc.length>0 & oldVal.sortDesc != val.sortDesc) {
                        data.order = order
                        data.sort = this.options.sortBy[0]
                        data.page = oldVal.page

                    }else{
                        data.page = val.page;
                    }

                    let obj = this.form;

                    if (this.form.search!==null) { data.search = this.form.search}
                    data.items_per_page = this.options.itemsPerPage;

                    let finalData = Object.assign(data, obj);

                    this.$inertia.get('/backoffice/trainers', pickBy(finalData), {preserveState: true});
                    this.options.page = this.members.current_page;
                }

            },
            deep: true,
        },

        birthDatePicker (val) {
        val && setTimeout(() => (this.activePicker = 'YEAR'))
      },
    },
    created() {
        this.initPageAndSort()
    },
    methods: {

        createNew() {
            this.saving = true
            axios.post('/backoffice/trainers/store', this.newTrainer)
                .then(response => {
                    this.saving = false;
                    if (response.data.status==="OK") {

                        this.newTrainerModal = false;
                        this.$inertia.visit('/backoffice/trainers/details/' + response.data.trainer.id, { method: 'get' })
                    }
                })
                .catch((error) => {
                    this.saving = false;
                    EventBus.$emit('SHOW_NOTIFICATION',{
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    this.responseErrors = error.response.data.errors
                })
        },

        initPageAndSort() {
            let pageUrl = new URLSearchParams(window.location.search)
            let sortDesc = pageUrl.get('order')
            let sortBy = pageUrl.get('sort')
            let page = pageUrl.get('page')
            this.page = page ? page : null
            sortBy ? this.sortBy[0] = sortBy : ''
            if (sortDesc) {
                sortDesc==='asc' ? this.sortDesc[0] = false : this.sortDesc[0] = true
            }
        },
        getGender(gender){
            if(gender === 0) return this.$page.props.lang.male || 'Male';
            else if(gender === 1) return this.$page.props.lang.female || 'Female';
            else return this.$page.props.lang.other || 'Other';
        }
    }
}

</script>
